import { useContext, useMemo } from 'react';

import { useAppSelector } from '@/hooks';
import { selectLookup } from '@/store/slices';

import { FormContext } from '../contexts';
import { useFormValues } from '../core';
import type {
  FieldsGroupAddressConfig,
  FieldsGroupAddressOption,
  FormInitialValues,
} from '../typings';
import {
  addressLinkFieldId,
  getAddressChoiceState,
  indexAddressGroups,
  isAddressGroupLive,
  resolveAddressOwner,
  type AddressChoiceState,
  type AddressGroupField,
} from '../utils/addressLink';
import {
  addressGroupTitle,
  nowPoBoxMessage,
  ownerHoldsPoBox,
  PO_BOX_NOT_ACCEPTED_HERE,
  refusesPoBox,
} from '../utils/addressPoBox';
import {
  describeAddressTarget,
  summarizeOwner,
  type AddressSummaryLookups,
} from '../utils/addressSummary';

/** The current state of a group's address source choice. */
export const useAddressChoice = (
  fieldId: string,
  address?: FieldsGroupAddressConfig,
): AddressChoiceState => {
  const values = useFormValues<FormInitialValues>();
  const rawValue = values[addressLinkFieldId(fieldId)];

  return useMemo(
    () => getAddressChoiceState(address, rawValue),
    [address, rawValue],
  );
};

/** The address a group ends up with, following its link; undefined while it has none. */
export const useEffectiveAddress = (
  fieldId: string,
  address?: FieldsGroupAddressConfig,
): string | undefined => {
  const { formFieldGroups } = useContext(FormContext);
  const values = useFormValues<FormInitialValues>();
  const lookups = useAppSelector(selectLookup);

  const index = useMemo(
    () => (address ? indexAddressGroups(formFieldGroups) : undefined),
    [address, formFieldGroups],
  );

  return useMemo(() => {
    if (!index) return undefined;

    const summary = summarizeOwner(
      resolveAddressOwner(fieldId, index, values),
      values,
      lookups,
    );

    return summary || undefined;
  }, [fieldId, index, values, lookups]);
};

/** One radio of the choice, as the provider sees it now. */
export type AddressOptionView = {
  value: string;
  text: string;
  enabled: boolean;
};

/** The choice's radios, and the notice under them while the link leads to a PO box the group turns away. */
export type AddressOptionViews = {
  views: AddressOptionView[];
  linkedPoBoxMessage?: string;
};

// Shown for a target that is not live or has no address yet, and for a
// position no entry holds.
const NOT_ENTERED = 'not entered yet';

const optionText = (
  label: string,
  preview: string | undefined,
  reason: string | undefined,
): string => {
  const text = `${label} (${preview || NOT_ENTERED})`;

  return reason ? `${text}: ${reason}` : text;
};

const buildOptionViews = (
  groupFieldId: string,
  address: FieldsGroupAddressConfig,
  choice: AddressChoiceState,
  index: ReadonlyMap<string, AddressGroupField>,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): AddressOptionViews => {
  const group = index.get(groupFieldId);
  // Only a group whose own street turns PO boxes away checks where its options lead.
  const refuses =
    group !== undefined && refusesPoBox(group, lookups.externalLookups);

  // Why an option can't be chosen for a PO box where it leads; undefined while it can.
  const poBoxReason = (
    option: FieldsGroupAddressOption,
  ): string | undefined => {
    // The client can't read another section's street, so the server's reason stands.
    if (!option.targetFieldId || !index.has(option.targetFieldId)) {
      return option.disabledReason;
    }
    if (!refuses) return undefined;

    const owner = resolveAddressOwner(option.targetFieldId, index, values);

    // Nor the street at the end of a chain that leaves this form.
    if (owner.kind === 'server') return option.disabledReason;

    return ownerHoldsPoBox(owner, values, lookups)
      ? PO_BOX_NOT_ACCEPTED_HERE
      : undefined;
  };

  const views = address.options.map((option): AddressOptionView => {
    if (option.target === 'Own') {
      return { value: option.value, text: option.label, enabled: true };
    }

    const reason = poBoxReason(option);

    // Not in this form, or blocked by a stored loop the client can't see: only the server knows.
    if (!option.targetFieldId || !index.has(option.targetFieldId)) {
      return {
        value: option.value,
        text: optionText(option.label, option.preview, reason),
        enabled: option.enabled && !reason,
      };
    }

    const { name, address: line } = describeAddressTarget(
      option.targetFieldId,
      index,
      values,
      lookups,
    );
    // A name alone is no address to reuse, so it shows only beside one.
    const preview = line && name ? `${name}, ${line}` : line;

    return {
      value: option.value,
      text: optionText(option.label, preview, reason),
      enabled: line !== '' && !reason,
    };
  });

  const linkedPoBoxMessage = (): string | undefined => {
    if (!choice.isLinked || !group || !isAddressGroupLive(group, values)) {
      return undefined;
    }

    const selected = address.options.find(
      option => option.value === choice.selectedValue,
    );
    const target = selected?.targetFieldId
      ? index.get(selected.targetFieldId)
      : undefined;

    if (selected && target) {
      return poBoxReason(selected)
        ? nowPoBoxMessage(addressGroupTitle(target))
        : undefined;
    }

    // The server's notice speaks for the saved answer only, so a choice made since drops it until the reload.
    return choice.selectedValue === address.linkValue
      ? address.linkedPoBoxMessage
      : undefined;
  };

  return { views, linkedPoBoxMessage: linkedPoBoxMessage() };
};

/** The choice's options with previews read from the form's current values, and its PO box notice. */
export const useAddressOptionViews = (
  groupFieldId: string,
  address: FieldsGroupAddressConfig,
  choice: AddressChoiceState,
): AddressOptionViews => {
  const { formFieldGroups } = useContext(FormContext);
  const values = useFormValues<FormInitialValues>();
  const lookups = useAppSelector(selectLookup);

  const index = useMemo(
    () => indexAddressGroups(formFieldGroups),
    [formFieldGroups],
  );

  return useMemo(
    () =>
      buildOptionViews(groupFieldId, address, choice, index, values, lookups),
    [groupFieldId, address, choice, index, values, lookups],
  );
};
